import { memo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ORDER_STATUS } from "@/constants/orderConstants";
import { getStatusColor } from "@/lib/statusColors";
import { 
  Sparkles, 
  CheckCircle, 
  Truck, 
  PackageCheck, 
  XCircle, 
  AlertTriangle,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  status: string;
  onClick?: () => void;
  interactive?: boolean;
  /** Compact mode: text-only colored label, no icon/pill/border */
  compact?: boolean;
}

// Status icon configuration (colors come from statusColors.ts)
const statusIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  [ORDER_STATUS.NEW]: Sparkles,
  'Processing': Sparkles,
  [ORDER_STATUS.COMPLETED]: CheckCircle,
  [ORDER_STATUS.OUT_FOR_DELIVERY]: Truck,
  [ORDER_STATUS.DELIVERED]: PackageCheck,
  [ORDER_STATUS.DELIVERY_FAILED]: AlertTriangle,
  [ORDER_STATUS.CANCELLED]: XCircle,
  'Awaiting Payment': Clock,
};

// Convert status to sentence case
const toSentenceCase = (status: string): string => {
  if (!status) return '';
  return status
    .split(' ')
    .map((word, index) => {
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      if (['for', 'of', 'the', 'a', 'an'].includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export const StatusBadge = memo(function StatusBadge({ 
  status, 
  onClick, 
  interactive = false,
  compact = false,
}: StatusBadgeProps) {
  const { t } = useLanguage();
  const { hsl } = getStatusColor(status);
  const color = `hsl(${hsl})`;
  const Icon = statusIcons[status] || statusIcons[ORDER_STATUS.NEW];

  const translatedLabel = t(`orders.statuses.${status.toLowerCase()}`);
  const displayLabel = toSentenceCase(translatedLabel);

  if (compact) {
    return (
      <span
        className={cn(
          "text-xs font-medium inline-block max-w-[110px] truncate align-middle transition-colors duration-200",
          interactive && "cursor-pointer hover:opacity-80"
        )}
        style={{ color }}
        onClick={onClick}
      >
        {displayLabel}
      </span>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-full",
        "text-[12px] font-medium leading-none",
        "transition-all duration-200",
        interactive && "cursor-pointer hover:opacity-80"
      )}
      style={{
        backgroundColor: `hsl(${hsl} / 0.1)`,
        border: `1px solid hsl(${hsl} / 0.3)`,
        color: color,
      }}
      onClick={onClick}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="whitespace-nowrap">{displayLabel}</span>
    </div>
  );
});
